import React, { useEffect, useRef, useState } from 'react';

/**
 * Cinematic custom cursor:
 *  - outer ring follows mouse with easing
 *  - inner dot snaps immediately
 *  - scales on [data-cursor="hover"] elements
 *  - disabled on touch devices
 */
const Cursor = () => {
  const ringRef = useRef(null);
  const dotRef  = useRef(null);
  const pos     = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!canHover) return;
    setEnabled(true);
    document.documentElement.classList.add('has-custom-cursor');

    const onMove = (e) => {
      pos.current.tx = e.clientX;
      pos.current.ty = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX - 3}px, ${e.clientY - 3}px, 0)`;
      }
    };
    const onEnter = (e) => {
      if (e.target?.closest && e.target.closest('[data-cursor="hover"], a, button, [role="button"], input, textarea, select, label')) {
        setHovering(true);
      }
    };
    const onLeave = () => setHovering(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseover', onEnter, { passive: true });
    document.addEventListener('mouseout', onLeave, { passive: true });

    let raf;
    const loop = () => {
      const p = pos.current;
      p.x += (p.tx - p.x) * 0.18;
      p.y += (p.ty - p.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${p.x - 18}px, ${p.y - 18}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', onEnter);
      document.removeEventListener('mouseout', onLeave);
      document.documentElement.classList.remove('has-custom-cursor');
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999] w-9 h-9 rounded-full border border-white/50 mix-blend-difference transition-[width,height,border-color,opacity] duration-300"
        style={{
          width: hovering ? 56 : 36,
          height: hovering ? 56 : 36,
          marginLeft: hovering ? -10 : 0,
          marginTop: hovering ? -10 : 0,
          borderColor: hovering ? 'rgba(34,211,238,0.9)' : 'rgba(255,255,255,0.5)',
          boxShadow: hovering ? '0 0 40px rgba(34,211,238,0.4)' : 'none',
        }}
      />
      <div
        ref={dotRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999] w-1.5 h-1.5 rounded-full bg-white"
      />
    </>
  );
};

export default Cursor;
